import Portrait from '../Portrait'
import { displayPosition, positionFill } from '../board/positions'
import Dossier from './Dossier'
import type { NewsItem, PlayerSummary } from '../../types/protocol'

// The laptop's dossier container: a persistent 18rem column instead of a
// drawer you have to open. The phone's scarce axis is vertical and the pick bar
// is already under the thumb; at lg there is 18rem of room sitting idle and the
// drafter's hands are on the search field, so making them reach for a chevron
// to read an injury note is the interaction the breakpoint exists to avoid.
//
// It shares Dossier — the same body the drawer renders — so the two surfaces
// cannot drift into showing different facts. Everything here is container:
// panel chrome, the identity header, and a taller news limit.
//
// No lg:sticky and no max-h. The sticky header stack has no fixed height
// (DraftHeader grows and shrinks with isMyPick / upNext / myNext), so there is
// no constant to offset top: by, and a wrong one parks the panel under the
// header. The grid is already lg:items-start and the panel is short.

interface Props {
  player: PlayerSummary | null
  news: NewsItem[]
  newsLoading: boolean
  newsUnavailable: boolean
}

export default function ScoutPanel({ player, news, newsLoading, newsUnavailable }: Props) {
  return (
    <section className="panel p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="eyebrow">Scout</h2>
      </div>

      {player == null ? (
        <p className="mt-2 text-xs text-ink-lo">
          Select a player to see their vitals and recent news.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          {/* Its own identity header, which the drawer does not need: the panel
              sits 18rem away from the pool row that produced it, and without a
              name in it the vitals strip is unattributed data. */}
          <div className="flex items-center gap-2.5">
            <Portrait
              fullName={player.full_name}
              position={player.position}
              nflTeam={player.nfl_team}
              src={player.portrait_url}
              size="lg"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span
                  className={`${positionFill(player.position)} text-pos-ink w-8 shrink-0 rounded-sm py-0.5 text-center font-mono text-[10px] font-semibold`}
                >
                  {displayPosition(player.position)}
                </span>
                <span className="min-w-0 flex-1 truncate font-display text-base leading-tight font-bold text-ink-hi">
                  {player.full_name}
                </span>
              </div>
              <p className="mt-0.5 font-mono text-[10.5px] text-ink-lo tabular-nums">
                {player.nfl_team ?? 'FA'} · #{player.effective_rank}
              </p>
            </div>
          </div>

          {/* newsLimit 5 where the drawer passes 3 — the one thing the two
              containers disagree about, and the reason the prop exists. */}
          <Dossier
            player={player}
            news={news}
            newsLoading={newsLoading}
            newsUnavailable={newsUnavailable}
            newsLimit={5}
          />
        </div>
      )}
    </section>
  )
}
